// Shared by desktop/mobile header, home finder and results search.
// No requests: only native GET query preparation and filtering of loaded cards.
export function buildQuery(value, { allowSku = true } = {}) {
  const term = String(value || '').trim().replace(/\s+/g, ' ');
  if (!term || term.length > 200) return null;
  if (allowSku && /^sku\s*:/i.test(term)) {
    const sku = term.replace(/^sku\s*:\s*/i, '').trim();
    if (!sku) return null;
    const escaped = sku.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return { term: `sku: ${sku}`, q: `variants.sku:"${escaped}"`, type: 'product', prefix: 'none' };
  }
  const tokens = term.normalize('NFD').replace(/[\u0300-\u036f]/g, '').match(/[A-Za-z0-9]+/g) || [];
  if (!tokens.length) return null;
  return { term, q: tokens.map(titleClause).join(' AND '), type: 'product', prefix: 'none' };
}

// A token that mixes letters and digits (XR150L, 150L, CRF250) also matches the
// same model written with spaces (XR 150 L). Plain tokens stay exactly as before.
export function titleClause(token) {
  const parts = token.match(/[A-Za-z]+|[0-9]+/g) || [token];
  if (parts.length < 2) return `title:${token}`;
  return `(title:${token} OR (${parts.map(part => `title:${part}`).join(' AND ')}))`;
}

export function deriveView(lines, line, expanded, mobile, serverTotal) {
  const limit = mobile ? 6 : 8;
  const matching = lines.map((value, index) => ({ value, index })).filter(item => line === 'all' || item.value === line);
  const visible = expanded ? matching : matching.slice(0, limit);
  return { loaded: lines.length, matching: matching.length, visible: visible.length,
    visibleIndexes: new Set(visible.map(item => item.index)), remaining: Math.max(0, matching.length - limit),
    total: Number.isFinite(Number(serverTotal)) ? Number(serverTotal) : null };
}

export function installSearch(doc, win) {
  // Section scripts may be evaluated repeatedly in the theme editor. Bind once.
  if (doc.__fmxSearchInstalled) return doc.__fmxSearchInstalled;
  const states = new WeakMap();
  const initialLine = () => {
    const line = new URL(win.location.href).searchParams.get('fmx_line');
    return ['next', 'fmx'].includes(line) ? line : 'all';
  };
  const text = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const lineLabel = line => ({ all: 'Todas', next: 'Next Covers', fmx: 'FMX Covers' })[line] || 'Todas';
  const render = root => {
    const state = states.get(root) || { line: initialLine(), expanded: false };
    states.set(root, state);
    // Read again after append/prepend, facets, section replacement or history.
    const cards = [...root.querySelectorAll('[data-fmx-product-line]')];
    const view = deriveView(cards.map(card => card.dataset.fmxProductLine), state.line, state.expanded,
      win.matchMedia('(max-width: 820px)').matches, root.dataset.fmxServerTotal);
    cards.forEach((card, index) => { card.hidden = !view.visibleIndexes.has(index); });
    root.querySelectorAll('[data-line]').forEach(button => {
      // A line absent from page 1 may be present on a later page.
      button.hidden = false;
      const active = button.dataset.line === state.line;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    const more = root.querySelector('[data-fmx-results-more]');
    const wrap = root.querySelector('[data-fmx-results-more-wrap]');
    if (wrap) wrap.hidden = view.remaining === 0;
    if (more) {
      more.hidden = view.remaining === 0;
      more.setAttribute('aria-expanded', String(state.expanded));
      text(more, state.expanded ? 'Ver menos resultados cargados' : `Ver ${view.remaining} resultado${view.remaining === 1 ? '' : 's'} cargado${view.remaining === 1 ? '' : 's'} más`);
    }
    const total = view.total === null ? 'desconocido' : String(view.total);
    const detail = state.line === 'all'
      ? `Mostrando ${view.visible} de ${view.loaded} productos cargados. Total del servidor: ${total}.`
      : `${lineLabel(state.line)}: mostrando ${view.visible} de ${view.matching} coincidencias entre ${view.loaded} productos cargados. Total del servidor (todas las líneas): ${total}.`;
    text(root.querySelector('[data-fmx-results-status]'), detail + (view.total === null || view.loaded < view.total
      ? ' El filtro sólo cubre los productos cargados; pueden aparecer más al cargar otras páginas.' : ''));
    if (view.total !== null) doc.querySelectorAll('[data-fmx-server-count]').forEach(node => text(node, total));
    const nextPage = root.querySelector('[data-fmx-next-page]');
    const grid = root.querySelector('[data-last-page]');
    if (nextPage && grid && cards.length) {
      const page = Math.max(...cards.map(card => Number(card.dataset.page) || 1)) + 1;
      const url = new URL(nextPage.href, win.location.href);
      url.searchParams.set('page', String(page));
      if (state.line === 'all') url.searchParams.delete('fmx_line');
      else url.searchParams.set('fmx_line', state.line);
      nextPage.href = url.href;
      nextPage.hidden = page > Number(grid.dataset.lastPage);
    }
  };
  const reconcile = () => doc.querySelectorAll('[data-fmx-results]').forEach(render);
  doc.addEventListener('submit', event => {
    const form = event.target.closest?.('form[data-fmx-search-form]');
    if (!form) return;
    const field = name => form.elements.namedItem(name);
    const home = form.hasAttribute('data-fmx-home-finder');
    const term = home ? [field('brand')?.value, field('model')?.value].filter(Boolean).join(' ').trim() : field('term')?.value;
    if (home && !term) {
      event.preventDefault();
      win.location.assign(field('vehicle')?.value === 'atv' ? form.dataset.fmxEmptyAtv : form.dataset.fmxEmptyMoto);
      return;
    }
    const query = buildQuery(term, { allowSku: !home });
    if (!query) { event.preventDefault(); (field('term') || field('model'))?.focus(); return; }
    field('q').value = query.q;
    field('type').value = query.type;
    field('options[prefix]').value = query.prefix;
    if (!home) field('term').value = query.term;
  });
  doc.addEventListener('click', event => {
    const button = event.target.closest?.('[data-line], [data-fmx-results-more]');
    const root = button?.closest('[data-fmx-results]');
    if (!root) return;
    const state = states.get(root) || { line: 'all', expanded: false };
    if (button.hasAttribute('data-line')) {
      if (!['all', 'next', 'fmx'].includes(button.dataset.line)) return;
      state.line = button.dataset.line;
      state.expanded = false;
      const url = new URL(win.location.href);
      if (state.line === 'all') url.searchParams.delete('fmx_line');
      else url.searchParams.set('fmx_line', state.line);
      win.history.replaceState(win.history.state, '', url.href);
    } else state.expanded = !state.expanded;
    states.set(root, state);
    render(root);
  });
  win.addEventListener('resize', reconcile);
  win.addEventListener('pageshow', reconcile);
  win.addEventListener('popstate', () => {
    doc.querySelectorAll('[data-fmx-results]').forEach(root => states.set(root, { line: initialLine(), expanded: false }));
    reconcile();
  });
  doc.addEventListener('shopify:section:load', reconcile);
  const observer = new win.MutationObserver(reconcile);
  observer.observe(doc.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-fmx-server-total'] });
  doc.__fmxSearchInstalled = { reconcile, observer };
  reconcile();
  return doc.__fmxSearchInstalled;
}

if (typeof document !== 'undefined') installSearch(document, window);

